// ============================================================================
// Python du lycée — exécution TRACÉE (refonte lot 5.7, moteur v28).
// ============================================================================
// Un interpréteur sûr : aucun `eval`, aucun accès au système, un budget d'étapes,
// de profondeur d'appels, de taille des nombres et des listes. Les nombres sont
// exacts (cf. value.ts).
//
// Pendant l'exécution, la fonction SUIVIE (l'appel de la question, sinon le
// programme lui-même) laisse une TRACE :
//   - un POINT après chaque instruction qui change ses variables (affectation, tour
//     de `for`, `L.append(…)`), et à son `return` ;
//   - des fins d'UNITÉ (`end`) : la fin d'un tour de boucle, d'un `if` du corps, ou
//     d'une suite d'instructions simples du corps (« u = 1 ; n = 0 » = une unité) ;
//   - les CONDITIONS évaluées (if, elif, while) avec la valeur de leurs deux membres ;
//   - les APPELS de fonctions de l'élève faits par la fonction suivie (« f(1) »).
// C'est ce que l'élève écrit sur sa feuille de trace, un tour à la fois.
// ============================================================================

import * as Q from '../exact';
import type { Rat } from '../exact';
import type { BinOp, CmpOp, Expr, Module, Stmt, Target } from './ast';
import { isCompound } from './ast';
import { PyError } from './errors';
import { MersenneTwister } from './mt';
import { parseExpression, parseProgram } from './parser';
import {
  clone,
  formatValue,
  isNumeric,
  NONE,
  numOf,
  pyBool,
  pyEq,
  pyFloat,
  pyInt,
  typeName,
  type PyValue,
} from './value';

export type UnitKind = 'block' | 'iteration' | 'branch' | 'return';

export interface TracePoint {
  kind: 'start' | 'assign' | 'return';
  /** ligne du programme (0 pour le départ) */
  line: number;
  /** variables de la fonction suivie à ce moment (copies) */
  vars: Record<string, PyValue>;
  /** ce point termine une unité (tour de boucle, bloc, if, return) */
  end: boolean;
  unit?: UnitKind;
  /** valeur renvoyée (point `return`) */
  ret?: PyValue;
  /** fin d'un tour : l'exécution de boucle à laquelle il appartient (moteur v29) */
  loop?: number;
}

export interface CondEvent {
  /** index du dernier point avant le test */
  after: number;
  line: number;
  /** test d'une boucle `while` (sinon if / elif) */
  loop: boolean;
  result: boolean;
  /** le test écrit (« u < 100 ») */
  text: string;
  /** une seule comparaison : valeurs de ses deux membres */
  cmp: { l: PyValue; op: CmpOp; r: PyValue } | null;
}

export interface CallEvent {
  after: number;
  name: string;
  /** « f(1,5) » : l'appel tel que l'élève l'écrit */
  atom: string;
  value: PyValue;
}

/** un calcul fait par la fonction suivie (« n % 2 » → 1) : moteur v29 */
export interface EvalEvent {
  after: number;
  line: number;
  op: BinOp;
  l: PyValue;
  r: PyValue;
  v: PyValue;
  /** le calcul écrit dans le programme (« n % 2 ») */
  text: string;
}

export interface FocusCall {
  name: string;
  args: PyValue[];
  atom: string;
  value: PyValue | null;
}

export interface ProgramTrace {
  ok: boolean;
  error: PyError | null;
  points: TracePoint[];
  conds: CondEvent[];
  calls: CallEvent[];
  focus: FocusCall | null;
  output: string[];
  steps: number;
  /** variables du programme à la fin */
  globals: Record<string, PyValue>;
  /** calculs (opérations sur des nombres) de la fonction suivie, dans l'ordre */
  evals: EvalEvent[];
  /** tirages imposés consommés */
  drawsUsed: number;
}

export interface RunOptions {
  /** appel suivi, évalué après le programme : « pgcd(84, 36) » */
  call?: string;
  maxSteps?: number;
  /**
   * tirages IMPOSÉS (énoncé : « on suppose que randint renvoie 4, puis 1 ») : chaque
   * appel à randint, random, choice, randrange, uniform prend le suivant
   */
  draws?: PyValue[];
  /** graine du générateur quand le programme n'appelle pas `random.seed` (défaut 0) */
  seed?: bigint;
  /** false : exécuter sans garder de trace (tests d'un programme complété, simulateur) */
  trace?: boolean;
}

/* ------------------------------ simulateur ------------------------------ */

export interface SimVar {
  name: string;
  /** écriture Python : « 2.5 », « True », « [1, 3] » */
  value: string;
  /** la valeur vient de changer (ou la variable d'apparaître) */
  changed: boolean;
}
export interface SimFrame {
  id: number;
  /** « Programme », ou l'appel en cours : « pgcd(84, 36) » */
  name: string;
  vars: SimVar[];
}
export interface SimStep {
  /** ligne surlignée (1 = première ligne du code ; 0 : aucune) */
  line: number;
  kind: 'stmt' | 'cond' | 'call' | 'return' | 'error' | 'end';
  /** ce qui vient de se passer, en clair */
  label: string;
  /** un test (if, elif, while) : son écriture et son issue */
  cond?: { text: string; result: boolean };
  /** la pile : le programme, puis chaque appel en cours */
  frames: SimFrame[];
  /** ce que print a affiché jusque-là */
  output: string[];
  /** valeur renvoyée (pas `return`) */
  ret?: string;
}
export interface Simulation {
  ok: boolean;
  error: PyError | null;
  steps: SimStep[];
  /** la simulation s'arrête au-delà de MAX_SIM_STEPS pas */
  truncated: boolean;
}
export const MAX_SIM_STEPS = 2000;
const MAX_EVALS = 5000;
const RANDOM_FUNCS = new Set([
  'randint',
  'random',
  'choice',
  'randrange',
  'uniform',
  'seed',
  'shuffle',
]);

export const DEFAULT_MAX_STEPS = 20000;
const MAX_POINTS = 1000;
const MAX_DEPTH = 100;
const MAX_LIST = 10000;
const MAX_BITS = 2048n;

interface Frame {
  vars: Map<string, PyValue>;
  focus: boolean;
}

interface Sim {
  steps: SimStep[];
  truncated: boolean;
  lines: string[];
}

type Flow = { k: 'normal' } | { k: 'break' } | { k: 'continue' } | { k: 'return'; v: PyValue };
const NORMAL: Flow = { k: 'normal' };

const BUILTINS = new Set([
  'print',
  'range',
  'len',
  'abs',
  'min',
  'max',
  'sum',
  'int',
  'float',
  'round',
  'str',
  'list',
  'sorted',
  'bool',
  'enumerate',
]);
const MATH_FUNCS = new Set(['sqrt', 'floor', 'ceil', 'fabs']);

/* ------------------------------ nombres ------------------------------ */

function floorRat(q: Rat): bigint {
  return q.n >= 0n ? q.n / q.d : -((-q.n + q.d - 1n) / q.d);
}
function ceilRat(q: Rat): bigint {
  return -floorRat(Q.neg(q));
}
function checkInt(v: bigint, line: number): bigint {
  const a = v < 0n ? -v : v;
  if (a.toString(2).length > Number(MAX_BITS))
    throw new PyError('value', 'Nombre trop grand.', line);
  return v;
}
function checkRat(q: Rat, line: number): Rat {
  checkInt(q.n, line);
  checkInt(q.d, line);
  return q;
}
/** racine carrée exacte d'un rationnel positif, sinon null */
function sqrtExact(q: Rat): Rat | null {
  if (q.n < 0n) return null;
  const r = (x: bigint): bigint | null => {
    if (x < 2n) return x;
    let y = BigInt(Math.floor(Math.sqrt(Number(x))));
    while (y * y > x) y--;
    while ((y + 1n) * (y + 1n) <= x) y++;
    return y * y === x ? y : null;
  };
  const a = r(q.n);
  const b = r(q.d);
  return a !== null && b !== null ? Q.rat(a, b) : null;
}

function truthy(x: PyValue): boolean {
  switch (x.t) {
    case 'bool':
      return x.v;
    case 'int':
      return x.v !== 0n;
    case 'float':
      return x.v.n !== 0n;
    case 'none':
      return false;
    case 'str':
      return x.v.length > 0;
    case 'list':
    case 'tuple':
      return x.items.length > 0;
    case 'range':
      return rangeLen(x) > 0n;
    default:
      return true;
  }
}

function rangeLen(r: Extract<PyValue, { t: 'range' }>): bigint {
  if (r.step > 0n) return r.stop > r.start ? (r.stop - r.start + r.step - 1n) / r.step : 0n;
  return r.start > r.stop ? (r.start - r.stop - r.step - 1n) / -r.step : 0n;
}

function isIntLike(x: PyValue): boolean {
  return x.t === 'int' || x.t === 'bool';
}
function intOf(x: PyValue): bigint {
  return x.t === 'int' ? x.v : x.t === 'bool' ? (x.v ? 1n : 0n) : 0n;
}

function arith(op: BinOp, a: PyValue, b: PyValue, line: number): PyValue {
  if (op === '+') {
    if (a.t === 'list' && b.t === 'list') return listOf([...a.items, ...b.items], line);
    if (a.t === 'str' && b.t === 'str') return { t: 'str', v: a.v + b.v };
    if (a.t === 'tuple' && b.t === 'tuple') return { t: 'tuple', items: [...a.items, ...b.items] };
  }
  if (op === '*') {
    const rep = (items: PyValue[], n: PyValue) => {
      const k = intOf(n);
      if (k * BigInt(items.length) > BigInt(MAX_LIST))
        throw new PyError('value', 'Liste trop longue.', line);
      const out: PyValue[] = [];
      for (let i = 0n; i < k; i++) out.push(...items.map(clone));
      return out;
    };
    if (a.t === 'list' && isIntLike(b)) return { t: 'list', items: rep(a.items, b) };
    if (b.t === 'list' && isIntLike(a)) return { t: 'list', items: rep(b.items, a) };
    if (a.t === 'str' && isIntLike(b))
      return { t: 'str', v: a.v.repeat(Math.max(0, Number(intOf(b)))) };
  }
  if (op === '%' && a.t === 'str')
    throw new PyError(
      'unsupported',
      'Le formatage de chaînes avec % n’est pas pris en charge.',
      line,
    );
  if (!isNumeric(a) || !isNumeric(b))
    throw new PyError(
      'type',
      `Opération « ${op} » impossible entre ${typeName(a)} et ${typeName(b)}.`,
      line,
    );
  if (isIntLike(a) && isIntLike(b)) {
    const x = intOf(a);
    const y = intOf(b);
    switch (op) {
      case '+':
        return pyInt(checkInt(x + y, line));
      case '-':
        return pyInt(checkInt(x - y, line));
      case '*':
        return pyInt(checkInt(x * y, line));
      case '/':
        if (y === 0n) throw new PyError('zero_division', 'Division par zéro.', line);
        return pyFloat(Q.rat(x, y));
      case '//':
        if (y === 0n) throw new PyError('zero_division', 'Division par zéro.', line);
        return pyInt(floorRat(Q.rat(x, y)));
      case '%': {
        if (y === 0n) throw new PyError('zero_division', 'Division par zéro (reste).', line);
        return pyInt(x - y * floorRat(Q.rat(x, y)));
      }
      case '**': {
        if (y < 0n) {
          if (x === 0n) throw new PyError('zero_division', '0 à une puissance négative.', line);
          if (-y > 4096n) throw new PyError('value', 'Exposant trop grand.', line);
          return pyFloat(checkRat(Q.pow(Q.rat(x), Number(y))!, line));
        }
        if (y > 4096n && x !== 0n && x !== 1n && x !== -1n)
          throw new PyError('value', 'Exposant trop grand.', line);
        return pyInt(checkInt(x ** (y > 4096n ? y % 2n : y), line));
      }
    }
  }
  const x = numOf(a)!;
  const y = numOf(b)!;
  switch (op) {
    case '+':
      return pyFloat(checkRat(Q.add(x, y), line));
    case '-':
      return pyFloat(checkRat(Q.sub(x, y), line));
    case '*':
      return pyFloat(checkRat(Q.mul(x, y), line));
    case '/': {
      const r = Q.div(x, y);
      if (!r) throw new PyError('zero_division', 'Division par zéro.', line);
      return pyFloat(checkRat(r, line));
    }
    case '//': {
      const r = Q.div(x, y);
      if (!r) throw new PyError('zero_division', 'Division par zéro.', line);
      return pyFloat(Q.rat(floorRat(r)));
    }
    case '%': {
      const r = Q.div(x, y);
      if (!r) throw new PyError('zero_division', 'Division par zéro (reste).', line);
      return pyFloat(checkRat(Q.sub(x, Q.mul(y, Q.rat(floorRat(r)))), line));
    }
    case '**': {
      if (Q.isInteger(y)) {
        const k = Number(y.n);
        if (Math.abs(k) > 4096) throw new PyError('value', 'Exposant trop grand.', line);
        const r = Q.pow(x, k);
        if (!r) throw new PyError('zero_division', '0 à une puissance négative.', line);
        return pyFloat(checkRat(r, line));
      }
      if (Q.eq(y, Q.rat(1, 2))) {
        const r = sqrtExact(x);
        if (!r)
          throw new PyError('inexact', 'Cette racine n’a pas de valeur décimale exacte.', line);
        return pyFloat(r);
      }
      throw new PyError('inexact', 'Puissance non entière : pas de valeur exacte.', line);
    }
  }
}

function compare(op: CmpOp, a: PyValue, b: PyValue, line: number): boolean {
  if (op === '==') return pyEq(a, b);
  if (op === '!=') return !pyEq(a, b);
  if (op === 'in' || op === 'not in') {
    let found: boolean;
    if (b.t === 'list' || b.t === 'tuple') found = b.items.some((v) => pyEq(v, a));
    else if (b.t === 'range') {
      const v = numOf(a);
      found =
        !!v &&
        Q.isInteger(v) &&
        (b.step > 0n ? v.n >= b.start && v.n < b.stop : v.n <= b.start && v.n > b.stop) &&
        (v.n - b.start) % b.step === 0n;
    } else if (b.t === 'str' && a.t === 'str') found = b.v.includes(a.v);
    else throw new PyError('type', `« in » impossible avec ${typeName(b)}.`, line);
    return op === 'in' ? found : !found;
  }
  const x = numOf(a);
  const y = numOf(b);
  let c: number;
  if (x && y) c = Q.cmp(x, y);
  else if (a.t === 'str' && b.t === 'str') c = a.v < b.v ? -1 : a.v > b.v ? 1 : 0;
  else
    throw new PyError(
      'type',
      `Comparaison impossible entre ${typeName(a)} et ${typeName(b)}.`,
      line,
    );
  switch (op) {
    case '<':
      return c < 0;
    case '>':
      return c > 0;
    case '<=':
      return c <= 0;
    case '>=':
      return c >= 0;
  }
}

function listOf(items: PyValue[], line: number): PyValue {
  if (items.length > MAX_LIST) throw new PyError('value', 'Liste trop longue.', line);
  return { t: 'list', items };
}

function iterate(x: PyValue, line: number): PyValue[] {
  if (x.t === 'list' || x.t === 'tuple') return x.items.slice();
  if (x.t === 'str') return [...x.v].map((c) => ({ t: 'str', v: c }) as PyValue);
  if (x.t === 'range') {
    const n = rangeLen(x);
    if (n > BigInt(MAX_LIST)) throw new PyError('budget', 'Boucle trop longue.', line);
    const out: PyValue[] = [];
    for (let i = 0n; i < n; i++) out.push(pyInt(x.start + i * x.step));
    return out;
  }
  throw new PyError('type', `On ne peut pas parcourir ${typeName(x)}.`, line);
}

function indexOf(seq: PyValue[], i: PyValue, line: number): number {
  if (!isIntLike(i)) throw new PyError('type', 'Un indice doit être un entier.', line);
  let k = Number(intOf(i));
  if (k < 0) k += seq.length;
  if (k < 0 || k >= seq.length) throw new PyError('index', 'Indice hors de la liste.', line);
  return k;
}

/* ------------------------------ texte ------------------------------ */

const PREC: Record<string, number> = {
  or: 1,
  and: 2,
  not: 3,
  cmp: 4,
  '+': 5,
  '-': 5,
  '*': 6,
  '/': 6,
  '//': 6,
  '%': 6,
  unary: 7,
  '**': 8,
};

/** écriture Python d'une expression (messages : « u < 100 ») */
export function exprText(e: Expr, parent = 0): string {
  const wrap = (s: string, p: number) => (p < parent ? `(${s})` : s);
  switch (e.k) {
    case 'num':
      return e.text;
    case 'str':
      return JSON.stringify(e.v);
    case 'const':
      return e.v;
    case 'name':
      return e.id;
    case 'list':
      return '[' + e.items.map((x) => exprText(x)).join(', ') + ']';
    case 'tuple':
      return '(' + e.items.map((x) => exprText(x)).join(', ') + ')';
    case 'comp':
      return `[${exprText(e.elt)} for … in ${exprText(e.iter)}]`;
    case 'unary':
      return e.op === 'not'
        ? wrap('not ' + exprText(e.e, PREC.not!), PREC.not!)
        : wrap(e.op + exprText(e.e, PREC.unary!), PREC.unary!);
    case 'bin': {
      const p = PREC[e.op]!;
      return wrap(`${exprText(e.l, p)} ${e.op} ${exprText(e.r, p + 1)}`, p);
    }
    case 'bool': {
      const p = PREC[e.op]!;
      return wrap(`${exprText(e.l, p)} ${e.op} ${exprText(e.r, p + 1)}`, p);
    }
    case 'compare':
      return wrap(
        [
          exprText(e.first, PREC.cmp! + 1),
          ...e.ops.map((o, i) => `${o} ${exprText(e.rest[i]!, PREC.cmp! + 1)}`),
        ].join(' '),
        PREC.cmp!,
      );
    case 'call':
      return `${exprText(e.f, 9)}(${e.args.map((a) => exprText(a)).join(', ')})`;
    case 'index':
      return `${exprText(e.e, 9)}[${exprText(e.i)}]`;
    case 'slice':
      return `${exprText(e.e, 9)}[${e.lo ? exprText(e.lo) : ''}:${e.hi ? exprText(e.hi) : ''}]`;
    case 'attr':
      return `${exprText(e.e, 9)}.${e.name}`;
    case 'ifexp':
      return `${exprText(e.body)} if ${exprText(e.test)} else ${exprText(e.orelse)}`;
  }
}

/** l'appel tel que l'élève l'écrit : « pgcd(84, 36) », « f(1,5) », « g(−4) » */
export function callAtom(name: string, args: PyValue[]): string {
  return `${name}(${args.map((a) => formatValue(a)).join(', ')})`;
}

/* ------------------------------ machine ------------------------------ */

class Machine {
  steps = 0;
  depth = 0;
  points: TracePoint[] = [];
  conds: CondEvent[] = [];
  calls: CallEvent[] = [];
  output: string[] = [];
  focus: FocusCall | null = null;
  /** nom de la fonction à suivre (appel de la question), pas encore entrée */
  wanted: string | null = null;
  readonly globals: Frame = { vars: new Map(), focus: false };
  evals: EvalEvent[] = [];
  loopSeq = 0;
  /** tirages imposés, et le prochain à donner */
  draws: PyValue[] | null = null;
  drawIdx = 0;
  rng: MersenneTwister | null = null;
  seedDefault = 0n;
  /** pile des appels (simulateur) */
  stack: { id: number; name: string; f: Frame }[] = [];
  frameSeq = 0;
  sim: Sim | null = null;

  constructor(private readonly maxSteps: number) {
    this.stack.push({ id: 0, name: 'Programme', f: this.globals });
  }

  tick(line: number): void {
    if (++this.steps > this.maxSteps)
      throw new PyError('budget', 'Le programme ne s’arrête pas (ou il est trop long).', line);
  }

  snapshot(f: Frame): Record<string, PyValue> {
    const out: Record<string, PyValue> = {};
    f.vars.forEach((v, k) => {
      if (v.t === 'func' || v.t === 'builtin' || v.t === 'module' || v.t === 'method') return;
      out[k] = clone(v);
    });
    return out;
  }
  /** garder la trace de la fonction suivie (false : tests, simulateur) */
  tracing = true;

  point(f: Frame, line: number, kind: TracePoint['kind'] = 'assign', ret?: PyValue): void {
    if (!f.focus || !this.tracing) return;
    if (this.points.length >= MAX_POINTS) throw new PyError('budget', 'Trace trop longue.', line);
    this.points.push({
      kind,
      line,
      vars: this.snapshot(f),
      end: kind === 'return',
      ...(kind === 'return' ? { unit: 'return' as const, ret: clone(ret!) } : {}),
    });
  }
  markEnd(f: Frame, from: number, unit: UnitKind, loop?: number): void {
    if (!f.focus || this.points.length <= from) return;
    const p = this.points[this.points.length - 1]!;
    if (p.end) return;
    p.end = true;
    p.unit = unit;
    if (loop) p.loop = loop;
  }

  /* ---- simulateur : un pas après chaque instruction, test, appel, retour ---- */

  simRec(
    kind: SimStep['kind'],
    line: number,
    label: string,
    extra: Partial<Pick<SimStep, 'cond' | 'ret'>> = {},
  ): void {
    const sim = this.sim;
    if (!sim) return;
    if (sim.steps.length >= MAX_SIM_STEPS) {
      sim.truncated = true;
      return;
    }
    const prev = sim.steps[sim.steps.length - 1];
    const frames: SimFrame[] = this.stack.map(({ id, name, f }) => {
      const before = prev?.frames.find((x) => x.id === id);
      const vars: SimVar[] = [];
      f.vars.forEach((v, k) => {
        if (v.t === 'func' || v.t === 'builtin' || v.t === 'module' || v.t === 'method') return;
        const value = formatValue(v, true);
        const old = before?.vars.find((x) => x.name === k);
        vars.push({ name: k, value, changed: !old || old.value !== value });
      });
      return { id, name, vars };
    });
    sim.steps.push({ line, kind, label, frames, output: this.output.slice(), ...extra });
  }

  srcLine(line: number): string {
    return (this.sim?.lines[line - 1] ?? '').trim();
  }

  lookup(f: Frame, id: string, line: number): PyValue {
    const v = f.vars.get(id) ?? this.globals.vars.get(id);
    if (v) return v;
    if (BUILTINS.has(id)) return { t: 'builtin', name: id };
    throw new PyError('name', `La variable « ${id} » n’existe pas (encore).`, line);
  }

  /* ---- instructions ---- */

  execBody(body: Stmt[], f: Frame, level: number): Flow {
    let run = -1;
    const top = f.focus && level === 0;
    for (let idx = 0; idx < body.length; idx++) {
      const s = body[idx]!;
      const simple = !isCompound(s) && s.k !== 'return';
      if (top) {
        if (simple) {
          if (run < 0) run = this.points.length;
        } else if (run >= 0) {
          this.markEnd(f, run, 'block');
          run = -1;
        }
      }
      const r = this.exec(s, f, level);
      if (r.k !== 'normal') {
        if (top && run >= 0) this.markEnd(f, run, 'block');
        return r;
      }
    }
    if (top && run >= 0) this.markEnd(f, run, 'block');
    return NORMAL;
  }

  exec(s: Stmt, f: Frame, level: number): Flow {
    this.tick(s.line);
    switch (s.k) {
      case 'pass':
        this.simRec('stmt', s.line, 'pass : rien à faire');
        return NORMAL;
      case 'break':
        this.simRec('stmt', s.line, 'break : on sort de la boucle');
        return { k: 'break' };
      case 'continue':
        this.simRec('stmt', s.line, 'continue : on passe au tour suivant');
        return { k: 'continue' };
      case 'import':
        this.doImport(s);
        this.simRec('stmt', s.line, this.srcLine(s.line));
        return NORMAL;
      case 'def':
        f.vars.set(s.name, { t: 'func', def: s });
        this.simRec(
          'stmt',
          s.line,
          `La fonction ${s.name} est définie (elle ne s’exécute pas encore)`,
        );
        return NORMAL;
      case 'expr': {
        const e = s.e;
        this.eval(e, f);
        // une méthode qui modifie une liste (« L.append(x) ») change l'état
        if (e.k === 'call' && e.f.k === 'attr') this.point(f, s.line);
        this.simRec('stmt', s.line, this.srcLine(s.line));
        return NORMAL;
      }
      case 'assign': {
        const v = this.eval(s.value, f);
        s.targets.forEach((t) => this.assign(t, s.targets.length > 1 ? clone(v) : v, f));
        this.point(f, s.line);
        this.simRec('stmt', s.line, this.srcLine(s.line));
        return NORMAL;
      }
      case 'aug': {
        const cur = this.eval(targetExpr(s.target), f);
        const v = this.eval(s.value, f);
        // L += [x] modifie la liste en place
        if (cur.t === 'list' && s.op === '+' && v.t === 'list') {
          cur.items.push(...v.items.map(clone));
          if (cur.items.length > MAX_LIST) throw new PyError('value', 'Liste trop longue.', s.line);
        } else this.assign(s.target, arith(s.op, cur, v, s.line), f);
        this.point(f, s.line);
        this.simRec('stmt', s.line, this.srcLine(s.line));
        return NORMAL;
      }
      case 'return': {
        const v = s.value ? this.eval(s.value, f) : NONE;
        this.point(f, s.line, 'return', v);
        const shown = formatValue(v, true);
        this.simRec('return', s.line, `La fonction renvoie ${shown}`, { ret: shown });
        return { k: 'return', v };
      }
      case 'if': {
        const from = this.points.length;
        const r = this.execIf(s, f, level);
        if (f.focus && level === 0) this.markEnd(f, from, 'branch');
        return r;
      }
      case 'while': {
        const loop = f.focus ? ++this.loopSeq : 0;
        for (;;) {
          if (!this.test(s.test, f, s.line, true)) return NORMAL;
          this.tick(s.line);
          const from = this.points.length;
          const r = this.execBody(s.body, f, level + 1);
          this.markEnd(f, from, 'iteration', loop);
          if (r.k === 'break') return NORMAL;
          if (r.k === 'return') return r;
        }
      }
      case 'for': {
        const loop = f.focus ? ++this.loopSeq : 0;
        const items = iterate(this.eval(s.iter, f), s.line);
        for (const it of items) {
          this.tick(s.line);
          const from = this.points.length;
          this.assign(s.target, it, f);
          this.point(f, s.line);
          this.simRec(
            'stmt',
            s.line,
            `Nouveau tour : ${targetText(s.target)} prend la valeur ${formatValue(it, true)}`,
          );
          const r = this.execBody(s.body, f, level + 1);
          this.markEnd(f, from, 'iteration', loop);
          if (r.k === 'break') return NORMAL;
          if (r.k === 'return') return r;
        }
        this.simRec('stmt', s.line, 'La boucle for est finie');
        return NORMAL;
      }
    }
  }

  execIf(s: Extract<Stmt, { k: 'if' }>, f: Frame, level: number): Flow {
    if (this.test(s.test, f, s.line, false)) return this.execBody(s.body, f, level + 1);
    if (s.orelse.length === 1 && s.orelse[0]!.k === 'if' && s.orelse[0]!.elif)
      return this.execIf(s.orelse[0] as Extract<Stmt, { k: 'if' }>, f, level);
    return s.orelse.length ? this.execBody(s.orelse, f, level + 1) : NORMAL;
  }

  /**
   * Issue d'un test et la comparaison qui la DÉCIDE (valeurs des deux membres) : la
   * comparaison elle-même, ou, pour « a or b » / « a and b » (moteur v29), l'opérande qui
   * décide, évalué en court-circuit comme Python (« âge ≥ 65 » décide « âge < 12 or
   * âge ≥ 65 » pour âge = 65). Même nombre d'évaluations que `eval`.
   */
  decide(e: Expr, f: Frame, line: number): { result: boolean; cmp: CondEvent['cmp'] } {
    if (e.k === 'compare' && e.ops.length === 1 && e.ops[0] !== 'in' && e.ops[0] !== 'not in') {
      const l = this.eval(e.first, f);
      const r = this.eval(e.rest[0]!, f);
      return {
        result: compare(e.ops[0]!, l, r, line),
        cmp: { l: clone(l), op: e.ops[0]!, r: clone(r) },
      };
    }
    if (e.k === 'bool') {
      const a = this.decide(e.l, f, line);
      if (e.op === 'or' ? a.result : !a.result) return a;
      return this.decide(e.r, f, line);
    }
    return { result: truthy(this.eval(e, f)), cmp: null };
  }

  /** évalue un test ; dans la fonction suivie, le note (valeurs des deux membres) */
  test(e: Expr, f: Frame, line: number, loop: boolean): boolean {
    const { result, cmp } = this.decide(e, f, line);
    if (f.focus && this.tracing)
      this.conds.push({
        after: this.points.length - 1,
        line,
        loop,
        result,
        text: exprText(e),
        cmp,
      });
    const text = exprText(e);
    this.simRec(
      'cond',
      line,
      `${text} est ${result ? 'vrai' : 'faux'} : ${
        loop
          ? result
            ? 'on fait un tour de boucle'
            : 'la boucle s’arrête'
          : result
            ? 'on exécute le bloc'
            : 'on saute le bloc'
      }`,
      { cond: { text, result } },
    );
    return result;
  }

  doImport(s: Extract<Stmt, { k: 'import' }>): void {
    const mods = s.from ? [s.from] : s.names;
    for (const mod of mods)
      if (mod !== 'math' && mod !== 'random')
        throw new PyError('unsupported', `Le module « ${mod} » n’est pas pris en charge.`, s.line);
    if (!s.from) {
      s.names.forEach((m) => this.globals.vars.set(m, { t: 'module', name: m }));
      return;
    }
    const known = s.from === 'math' ? MATH_FUNCS : RANDOM_FUNCS;
    const names = s.names[0] === '*' ? [...known] : s.names;
    names.forEach((n) => {
      if (!known.has(n))
        throw new PyError('unsupported', `${s.from}.${n} n’est pas pris en charge.`, s.line);
      this.globals.vars.set(n, { t: 'builtin', name: `${s.from}.${n}` });
    });
  }

  assign(t: Target, v: PyValue, f: Frame): void {
    if (t.k === 'name') {
      f.vars.set(t.id, v);
      return;
    }
    if (t.k === 'tuple') {
      const items = iterate(v, t.line);
      if (items.length !== t.items.length)
        throw new PyError(
          'value',
          `${t.items.length} variables pour ${items.length} valeurs.`,
          t.line,
        );
      t.items.forEach((x, i) => this.assign(x, items[i]!, f));
      return;
    }
    const seq = this.eval(t.e, f);
    if (seq.t !== 'list')
      throw new PyError('type', `On ne peut pas modifier ${typeName(seq)}.`, t.line);
    seq.items[indexOf(seq.items, this.eval(t.i, f), t.line)] = v;
  }

  /* ---- expressions ---- */

  eval(e: Expr, f: Frame): PyValue {
    switch (e.k) {
      case 'num': {
        if (/[.eE]/.test(e.text)) {
          const m = /^(\d*)\.?(\d*)(?:[eE]([+-]?\d+))?$/.exec(e.text)!;
          const digits = (m[1] || '0') + (m[2] ?? '');
          let q = Q.rat(BigInt(digits), 10n ** BigInt((m[2] ?? '').length));
          const ex = Number(m[3] ?? 0);
          if (Math.abs(ex) > 300) throw new PyError('value', 'Nombre trop grand.', e.line);
          if (ex) q = Q.mul(q, Q.pow(Q.rat(10), ex)!);
          return pyFloat(q);
        }
        return pyInt(BigInt(e.text));
      }
      case 'str':
        return { t: 'str', v: e.v };
      case 'const':
        return e.v === 'None' ? NONE : pyBool(e.v === 'True');
      case 'name':
        return this.lookup(f, e.id, e.line);
      case 'list':
        return listOf(
          e.items.map((x) => this.eval(x, f)),
          e.line,
        );
      case 'tuple':
        return { t: 'tuple', items: e.items.map((x) => this.eval(x, f)) };
      case 'comp': {
        const out: PyValue[] = [];
        const saved = new Map(f.vars);
        for (const it of iterate(this.eval(e.iter, f), e.line)) {
          this.tick(e.line);
          this.assign(e.target, it, f);
          if (!e.cond || truthy(this.eval(e.cond, f))) out.push(this.eval(e.elt, f));
        }
        // la variable d'une compréhension ne fuit pas (Python 3)
        f.vars.clear();
        saved.forEach((v, k) => f.vars.set(k, v));
        return listOf(out, e.line);
      }
      case 'unary': {
        const v = this.eval(e.e, f);
        if (e.op === 'not') return pyBool(!truthy(v));
        if (!isNumeric(v))
          throw new PyError('type', `« ${e.op} » impossible devant ${typeName(v)}.`, e.line);
        if (e.op === '+') return v.t === 'bool' ? pyInt(intOf(v)) : v;
        return v.t === 'float' ? pyFloat(Q.neg(v.v)) : pyInt(-intOf(v));
      }
      case 'bin': {
        const l = this.eval(e.l, f);
        const r = this.eval(e.r, f);
        const v = arith(e.op, l, r, e.line);
        // les calculs de la fonction suivie : l'élève peut en écrire un (« n % 2 = 1 »)
        if (
          f.focus &&
          this.tracing &&
          this.evals.length < MAX_EVALS &&
          isNumeric(l) &&
          isNumeric(r)
        )
          this.evals.push({
            after: this.points.length - 1,
            line: e.line,
            op: e.op,
            l: clone(l),
            r: clone(r),
            v: clone(v),
            text: exprText(e),
          });
        return v;
      }
      case 'bool': {
        const l = this.eval(e.l, f);
        if (e.op === 'and') return truthy(l) ? this.eval(e.r, f) : l;
        return truthy(l) ? l : this.eval(e.r, f);
      }
      case 'compare': {
        let l = this.eval(e.first, f);
        for (let i = 0; i < e.ops.length; i++) {
          const r = this.eval(e.rest[i]!, f);
          if (!compare(e.ops[i]!, l, r, e.line)) return pyBool(false);
          l = r;
        }
        return pyBool(true);
      }
      case 'ifexp':
        return truthy(this.eval(e.test, f)) ? this.eval(e.body, f) : this.eval(e.orelse, f);
      case 'index': {
        const s = this.eval(e.e, f);
        const i = this.eval(e.i, f);
        if (s.t === 'list' || s.t === 'tuple') return s.items[indexOf(s.items, i, e.line)]!;
        if (s.t === 'str')
          return {
            t: 'str',
            v: [...s.v][
              indexOf(
                [...s.v].map(() => NONE),
                i,
                e.line,
              )
            ]!,
          };
        throw new PyError('type', `On ne peut pas indexer ${typeName(s)}.`, e.line);
      }
      case 'slice': {
        const s = this.eval(e.e, f);
        if (s.t !== 'list' && s.t !== 'str')
          throw new PyError('type', `Tranche impossible sur ${typeName(s)}.`, e.line);
        const items: PyValue[] =
          s.t === 'list' ? s.items : [...s.v].map((c) => ({ t: 'str', v: c }) as PyValue);
        const n = items.length;
        const bound = (x: Expr | null, d: number) => {
          if (!x) return d;
          const v = this.eval(x, f);
          if (!isIntLike(v))
            throw new PyError('type', 'Une borne de tranche doit être un entier.', e.line);
          let k = Number(intOf(v));
          if (k < 0) k += n;
          return Math.min(Math.max(k, 0), n);
        };
        if (e.step)
          throw new PyError(
            'unsupported',
            'Les tranches avec un pas ne sont pas prises en charge.',
            e.line,
          );
        const out = items.slice(bound(e.lo, 0), bound(e.hi, n));
        return s.t === 'list'
          ? { t: 'list', items: out.map(clone) }
          : { t: 'str', v: out.map((c) => (c as { v: string }).v).join('') };
      }
      case 'attr': {
        const o = this.eval(e.e, f);
        if (o.t === 'module') {
          const known = o.name === 'math' ? MATH_FUNCS : RANDOM_FUNCS;
          if (!known.has(e.name))
            throw new PyError(
              'unsupported',
              `${o.name}.${e.name} n’est pas pris en charge.`,
              e.line,
            );
          return { t: 'builtin', name: `${o.name}.${e.name}` };
        }
        if (o.t === 'list') return { t: 'method', self: o, name: e.name };
        throw new PyError('unsupported', `« .${e.name} » n’est pas pris en charge.`, e.line);
      }
      case 'call':
        return this.call(e, f);
    }
  }

  call(e: Extract<Expr, { k: 'call' }>, f: Frame): PyValue {
    const fn = this.eval(e.f, f);
    const args = e.args.map((a) => this.eval(a, f));
    if (fn.t === 'func') {
      const v = this.callUser(fn.def, args, e.line, f);
      if (f.focus && this.tracing)
        this.calls.push({
          after: this.points.length - 1,
          name: fn.def.name,
          atom: callAtom(fn.def.name, args),
          value: v,
        });
      return v;
    }
    if (fn.t === 'builtin') return this.builtin(fn.name, args, e.line);
    if (fn.t === 'method') return this.method(fn.self, fn.name, args, e.line);
    throw new PyError('type', `${formatValue(fn)} n’est pas une fonction.`, e.line);
  }

  callUser(
    def: Extract<Stmt, { k: 'def' }>,
    args: PyValue[],
    line: number,
    caller: Frame,
  ): PyValue {
    if (args.length !== def.params.length)
      throw new PyError(
        'type',
        `${def.name} attend ${def.params.length} argument${def.params.length > 1 ? 's' : ''}, pas ${args.length}.`,
        line,
      );
    if (++this.depth > MAX_DEPTH) throw new PyError('recursion', 'Trop d’appels imbriqués.', line);
    const focus = this.wanted === def.name && !caller.focus && !this.focus;
    const frame: Frame = { vars: new Map(), focus };
    def.params.forEach((p, i) => frame.vars.set(p, args[i]!));
    const atom = `${def.name}(${args.map((a) => formatValue(a, true)).join(', ')})`;
    this.stack.push({ id: ++this.frameSeq, name: atom, f: frame });
    this.simRec('call', def.line, `Appel de ${atom}`);
    if (focus) {
      this.wanted = null;
      this.focus = {
        name: def.name,
        args: args.map(clone),
        atom: callAtom(def.name, args),
        value: null,
      };
      if (this.tracing)
        this.points.push({ kind: 'start', line: def.line, vars: this.snapshot(frame), end: true });
    }
    const r = this.execBody(def.body, frame, 0);
    this.depth--;
    const v = r.k === 'return' ? r.v : NONE;
    if (r.k !== 'return')
      this.simRec('return', def.line, 'Fin de la fonction, sans return : elle renvoie None', {
        ret: 'None',
      });
    this.stack.pop();
    if (focus) {
      // une fonction sans return renvoie None : un point de fin quand même
      if (r.k !== 'return' && this.tracing)
        this.points.push({
          kind: 'return',
          line: def.line,
          vars: this.snapshot(frame),
          end: true,
          unit: 'return',
          ret: NONE,
        });
      this.focus!.value = clone(v);
    }
    return v;
  }

  builtin(name: string, args: PyValue[], line: number): PyValue {
    const one = () => {
      if (args.length !== 1) throw new PyError('type', `${name} attend un argument.`, line);
      return args[0]!;
    };
    const nums = (xs: PyValue[]) =>
      xs.map((x) => {
        if (!isNumeric(x))
          throw new PyError('type', `${name} : ${typeName(x)} n’est pas un nombre.`, line);
        return x;
      });
    switch (name) {
      case 'print':
        this.output.push(args.map((a) => (a.t === 'str' ? a.v : formatValue(a, true))).join(' '));
        return NONE;
      case 'range': {
        if (!args.length || args.length > 3 || !args.every(isIntLike))
          throw new PyError('type', 'range attend 1 à 3 entiers.', line);
        const [a, b, c] = args.map(intOf);
        if (args.length === 1) return { t: 'range', start: 0n, stop: a!, step: 1n };
        if (c === 0n) throw new PyError('value', 'Le pas de range ne peut pas être 0.', line);
        return { t: 'range', start: a!, stop: b!, step: c ?? 1n };
      }
      case 'len': {
        const x = one();
        if (x.t === 'list' || x.t === 'tuple') return pyInt(x.items.length);
        if (x.t === 'str') return pyInt([...x.v].length);
        if (x.t === 'range') return pyInt(rangeLen(x));
        throw new PyError('type', `len impossible sur ${typeName(x)}.`, line);
      }
      case 'abs': {
        const x = nums([one()])[0]!;
        return x.t === 'float' ? pyFloat(Q.abs(x.v)) : pyInt(intOf(x) < 0n ? -intOf(x) : intOf(x));
      }
      case 'min':
      case 'max':
      case 'sum': {
        const xs = args.length === 1 ? iterate(args[0]!, line) : args;
        if (name === 'sum') return nums(xs).reduce((s, x) => arith('+', s, x, line), pyInt(0));
        if (!xs.length) throw new PyError('value', `${name} d’une liste vide.`, line);
        return xs.reduce((m, x) => {
          const lt = compare('<', x, m, line);
          return name === 'min' ? (lt ? x : m) : compare('>', x, m, line) ? x : m;
        });
      }
      case 'int': {
        const x = one();
        if (x.t === 'str') {
          if (!/^\s*[+-]?\d+\s*$/.test(x.v))
            throw new PyError('value', 'Ce n’est pas un entier.', line);
          return pyInt(BigInt(x.v.trim()));
        }
        const q = numOf(nums([x])[0]!)!;
        return pyInt(q.n >= 0n ? q.n / q.d : -(-q.n / q.d));
      }
      case 'float': {
        const x = one();
        if (x.t === 'str') {
          const q = Q.parseDecimal(x.v.replace(',', '#'));
          if (!q) throw new PyError('value', 'Ce n’est pas un nombre.', line);
          return pyFloat(q);
        }
        return pyFloat(numOf(nums([x])[0]!)!);
      }
      case 'round': {
        if (!args.length || args.length > 2)
          throw new PyError('type', 'round attend 1 ou 2 arguments.', line);
        const q = numOf(nums([args[0]!])[0]!)!;
        const k = args.length === 2 ? Number(intOf(args[1]!)) : 0;
        if (k < 0 || k > 20)
          throw new PyError('unsupported', 'round : nombre de chiffres non pris en charge.', line);
        const scale = 10n ** BigInt(k);
        const s = Q.mul(q, Q.rat(scale));
        // arrondi au plus proche, égalité vers le pair (comme Python)
        const fl = floorRat(s);
        const diff = Q.sub(s, Q.rat(fl));
        const c = Q.cmp(diff, Q.rat(1, 2));
        const r = c < 0 ? fl : c > 0 ? fl + 1n : fl % 2n === 0n ? fl : fl + 1n;
        if (args.length === 1) return pyInt(r);
        return args[0]!.t === 'float' ? pyFloat(Q.rat(r, scale)) : args[0]!;
      }
      case 'str':
        return { t: 'str', v: formatValue(one(), true) };
      case 'bool':
        return pyBool(truthy(one()));
      case 'list':
        return listOf(args.length ? iterate(one(), line).map(clone) : [], line);
      case 'sorted': {
        const xs = iterate(one(), line).map(clone);
        xs.sort((a, b) => (compare('<', a, b, line) ? -1 : compare('>', a, b, line) ? 1 : 0));
        return listOf(xs, line);
      }
      case 'enumerate':
        return listOf(
          iterate(one(), line).map((x, i) => ({ t: 'tuple', items: [pyInt(i), x] }) as PyValue),
          line,
        );
      case 'math.sqrt': {
        const q = numOf(nums([one()])[0]!)!;
        if (q.n < 0n) throw new PyError('value', 'Racine d’un nombre négatif.', line);
        const r = sqrtExact(q);
        if (!r)
          throw new PyError('inexact', 'Cette racine n’a pas de valeur décimale exacte.', line);
        return pyFloat(r);
      }
      case 'math.floor':
        return pyInt(floorRat(numOf(nums([one()])[0]!)!));
      case 'math.ceil':
        return pyInt(ceilRat(numOf(nums([one()])[0]!)!));
      case 'math.fabs':
        return pyFloat(Q.abs(numOf(nums([one()])[0]!)!));
    }
    if (name.startsWith('random.')) return this.randomCall(name.slice(7), args, line);
    throw new PyError('unsupported', `${name} n’est pas pris en charge.`, line);
  }

  /* ---- module random : tirages imposés, sinon le générateur de CPython ---- */

  generator(): MersenneTwister {
    if (!this.rng) this.rng = new MersenneTwister(this.seedDefault);
    return this.rng;
  }

  /** le prochain tirage imposé (null : pas de tirages imposés) */
  takeDraw(line: number): PyValue | null {
    if (!this.draws) return null;
    if (this.drawIdx >= this.draws.length)
      throw new PyError('value', `Plus de tirage : l’énoncé en donne ${this.draws.length}.`, line);
    return this.draws[this.drawIdx++]!;
  }

  randomCall(fn: string, args: PyValue[], line: number): PyValue {
    const ints = (k: number[]) => {
      if (!k.includes(args.length) || !args.every(isIntLike))
        throw new PyError('type', `${fn} attend des entiers.`, line);
      return args.map(intOf);
    };
    const drawNo = () => this.drawIdx;
    const bad = (d: PyValue, what: string) =>
      new PyError('value', `Le tirage n° ${drawNo()} (${formatValue(d)}) n’est pas ${what}.`, line);
    switch (fn) {
      case 'seed': {
        if (args.length > 1 || (args.length === 1 && !isIntLike(args[0]!)))
          throw new PyError('unsupported', 'random.seed : une graine entière.', line);
        if (!this.draws) this.generator().seed(args.length ? intOf(args[0]!) : 0n);
        return NONE;
      }
      case 'random': {
        if (args.length) throw new PyError('type', 'random() n’attend pas d’argument.', line);
        const d = this.takeDraw(line);
        if (d) {
          const q = numOf(d);
          if (!q || d.t === 'bool' || q.n < 0n || Q.cmp(q, Q.ONE) >= 0)
            throw bad(d, 'entre 0 (compris) et 1 (exclu)');
          return pyFloat(q);
        }
        const r = this.generator().random();
        return { t: 'float', v: Q.rat(r.n, r.d), bin: true };
      }
      case 'uniform': {
        if (args.length !== 2 || !args.every(isNumeric))
          throw new PyError('type', 'uniform attend deux nombres.', line);
        const a = numOf(args[0]!)!;
        const b = numOf(args[1]!)!;
        const d = this.takeDraw(line);
        if (d) {
          const q = numOf(d);
          const lo = Q.cmp(a, b) <= 0 ? a : b;
          const hi = Q.cmp(a, b) <= 0 ? b : a;
          if (!q || d.t === 'bool' || Q.cmp(q, lo) < 0 || Q.cmp(q, hi) > 0)
            throw bad(d, `entre ${formatValue(args[0]!)} et ${formatValue(args[1]!)}`);
          return pyFloat(q);
        }
        const r = this.generator().random();
        return pyFloat(Q.add(a, Q.mul(Q.sub(b, a), Q.rat(r.n, r.d))));
      }
      case 'randint':
      case 'randrange': {
        let start: bigint;
        let stop: bigint;
        let step = 1n;
        if (fn === 'randint') {
          const [a, b] = ints([2]);
          start = a!;
          stop = b! + 1n;
        } else {
          const xs = ints([1, 2, 3]);
          if (xs.length === 1) {
            start = 0n;
            stop = xs[0]!;
          } else {
            start = xs[0]!;
            stop = xs[1]!;
            step = xs[2] ?? 1n;
          }
        }
        if (step === 0n) throw new PyError('value', `${fn} : le pas ne peut pas être 0.`, line);
        const n = step > 0n ? (stop - start + step - 1n) / step : (stop - start + step + 1n) / step;
        if (n <= 0n) throw new PyError('value', `${fn} : intervalle vide.`, line);
        const d = this.takeDraw(line);
        if (d) {
          const k = isIntLike(d) && d.t !== 'bool' ? intOf(d) : null;
          const ok =
            k !== null &&
            (k - start) % step === 0n &&
            (k - start) / step >= 0n &&
            (k - start) / step < n;
          if (!ok)
            throw bad(
              d,
              fn === 'randint'
                ? `un entier entre ${start} et ${stop - 1n}`
                : `une valeur possible de ${fn}`,
            );
          return pyInt(k!);
        }
        return pyInt(start + step * this.generator().randbelow(n));
      }
      case 'choice': {
        if (args.length !== 1) throw new PyError('type', 'choice attend une liste.', line);
        const items = iterate(args[0]!, line);
        if (!items.length) throw new PyError('index', 'choice sur une liste vide.', line);
        const d = this.takeDraw(line);
        if (d) {
          const hit = items.find((x) => pyEq(x, d));
          if (!hit) throw bad(d, 'un élément de la liste');
          return hit;
        }
        return items[Number(this.generator().randbelow(BigInt(items.length)))]!;
      }
      case 'shuffle': {
        const L = args[0];
        if (args.length !== 1 || !L || L.t !== 'list')
          throw new PyError('type', 'shuffle attend une liste.', line);
        if (this.draws)
          throw new PyError(
            'unsupported',
            'shuffle avec des tirages imposés n’est pas pris en charge.',
            line,
          );
        const x = L.items;
        for (let i = x.length - 1; i > 0; i--) {
          const j = Number(this.generator().randbelow(BigInt(i + 1)));
          [x[i], x[j]] = [x[j]!, x[i]!];
        }
        return NONE;
      }
    }
    throw new PyError('unsupported', `random.${fn} n’est pas pris en charge.`, line);
  }

  method(self: PyValue, name: string, args: PyValue[], line: number): PyValue {
    if (self.t !== 'list')
      throw new PyError('unsupported', `« .${name} » n’est pas pris en charge.`, line);
    const L = self.items;
    switch (name) {
      case 'append':
        if (args.length !== 1) throw new PyError('type', 'append attend un argument.', line);
        if (L.length >= MAX_LIST) throw new PyError('value', 'Liste trop longue.', line);
        L.push(args[0]!);
        return NONE;
      case 'pop': {
        if (!L.length) throw new PyError('index', 'pop sur une liste vide.', line);
        const k = args.length ? indexOf(L, args[0]!, line) : L.length - 1;
        return L.splice(k, 1)[0]!;
      }
      case 'insert': {
        if (args.length !== 2 || !isIntLike(args[0]!))
          throw new PyError('type', 'insert attend un indice et une valeur.', line);
        let k = Number(intOf(args[0]!));
        if (k < 0) k = Math.max(0, k + L.length);
        L.splice(Math.min(k, L.length), 0, args[1]!);
        return NONE;
      }
      case 'remove': {
        const k = L.findIndex((x) => pyEq(x, args[0]!));
        if (k < 0) throw new PyError('value', 'Valeur absente de la liste.', line);
        L.splice(k, 1);
        return NONE;
      }
      case 'count':
        return pyInt(L.filter((x) => pyEq(x, args[0]!)).length);
      case 'index': {
        const k = L.findIndex((x) => pyEq(x, args[0]!));
        if (k < 0) throw new PyError('value', 'Valeur absente de la liste.', line);
        return pyInt(k);
      }
      case 'reverse':
        L.reverse();
        return NONE;
      case 'sort':
        L.sort((a, b) => (compare('<', a, b, line) ? -1 : compare('>', a, b, line) ? 1 : 0));
        return NONE;
    }
    throw new PyError('unsupported', `« .${name} » n’est pas pris en charge.`, line);
  }
}

function targetText(t: Target): string {
  if (t.k === 'name') return t.id;
  if (t.k === 'index') return exprText({ k: 'index', e: t.e, i: t.i, line: t.line });
  return t.items.map(targetText).join(', ');
}

function targetExpr(t: Target): Expr {
  if (t.k === 'name') return { k: 'name', id: t.id, line: t.line };
  if (t.k === 'index') return { k: 'index', e: t.e, i: t.i, line: t.line };
  return { k: 'tuple', items: t.items.map(targetExpr), line: t.line };
}

/**
 * Exécute le programme (puis l'appel `call`) en le traçant. Jamais d'exception :
 * une erreur du programme est rendue dans `error` (avec la trace jusque-là).
 */
export function runProgram(code: string, opts: RunOptions = {}): ProgramTrace {
  const m = new Machine(opts.maxSteps ?? DEFAULT_MAX_STEPS);
  const error = execute(m, code, opts);
  return {
    ok: !error,
    error,
    points: m.points,
    conds: m.conds,
    calls: m.calls,
    focus: m.focus,
    output: m.output,
    steps: m.steps,
    globals: m.snapshot(m.globals),
    evals: m.evals,
    drawsUsed: m.drawIdx,
  };
}

function execute(m: Machine, code: string, opts: RunOptions): PyError | null {
  let error: PyError | null = null;
  if (opts.draws) m.draws = opts.draws;
  if (opts.seed !== undefined) m.seedDefault = opts.seed;
  if (opts.trace === false) m.tracing = false;
  try {
    const mod: Module = parseProgram(code);
    let callExpr: Expr | null = null;
    if (opts.call) {
      callExpr = parseExpression(opts.call);
      if (callExpr.k !== 'call' || callExpr.f.k !== 'name')
        throw new PyError('syntax', 'L’appel suivi doit s’écrire « f(…) ».', 1);
    } else {
      m.globals.focus = true;
      if (m.tracing) m.points.push({ kind: 'start', line: 0, vars: {}, end: true });
    }
    m.execBody(mod.body, m.globals, 0);
    if (callExpr && callExpr.k === 'call' && callExpr.f.k === 'name') {
      // la fonction suivie : son premier appel PAR l'appel de la question
      m.wanted = callExpr.f.id;
      m.eval(callExpr, m.globals);
      if (!m.focus) throw new PyError('name', `La fonction « ${m.wanted} » n’est pas définie.`, 1);
    }
  } catch (e) {
    if (!(e instanceof PyError)) throw e;
    error = e;
  }
  return error;
}

/**
 * Simulateur pas à pas (piste I1) : l'exécution découpée en pas — une instruction, un
 * test (vrai ou faux), un appel, un retour —, avec à chaque pas la pile des variables
 * (celles qui viennent de changer marquées) et ce que `print` a affiché.
 */
export function simulateProgram(code: string, opts: RunOptions = {}): Simulation {
  const m = new Machine(opts.maxSteps ?? DEFAULT_MAX_STEPS);
  m.sim = { steps: [], truncated: false, lines: code.split('\n') };
  m.tracing = false;
  const error = execute(m, code, opts);
  if (error) m.simRec('error', error.line, error.message);
  else m.simRec('end', 0, 'Le programme est fini');
  return { ok: !error, error, steps: m.sim.steps, truncated: m.sim.truncated };
}

/** Valeur d'un appel d'une fonction du programme, sans trace (« f(1,5) » écrit par l'élève). */
export function evalCall(
  code: string,
  call: string,
  maxSteps = DEFAULT_MAX_STEPS,
  opts: Pick<RunOptions, 'draws' | 'seed'> = {},
): PyValue | null {
  const t = runProgram(code, { call, maxSteps, ...opts });
  return t.ok && t.focus ? t.focus.value : null;
}
